import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { TrendPoint } from '../reportMockData';
import { series, chrome, chartFont } from '../reportTheme';
import ChartTooltip from './ChartTooltip';

const axisTick = { fill: chrome.muted, fontSize: 12, fontFamily: chartFont };

interface TrendChartProps {
  data: TrendPoint[];
}

const TrendChart: React.FC<TrendChartProps> = ({ data }) => {
  const { t } = useTranslation('templates');
  const raisedLabel = t('report.trend.raised');
  const resolvedLabel = t('report.trend.resolved');

  const rows = data.map((p) => ({
    period: p.period,
    [raisedLabel]: p.raised,
    [resolvedLabel]: p.resolved,
  }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <defs>
          <linearGradient id="fillRaised" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={series.blue} stopOpacity={0.22} />
            <stop offset="100%" stopColor={series.blue} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="fillResolved" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={series.aqua} stopOpacity={0.22} />
            <stop offset="100%" stopColor={series.aqua} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={chrome.grid} vertical={false} />
        <XAxis dataKey="period" tick={axisTick} tickLine={false} axisLine={{ stroke: chrome.axis }} />
        <YAxis tick={axisTick} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
        <Tooltip content={<ChartTooltip />} cursor={{ stroke: chrome.axis, strokeWidth: 1 }} />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontFamily: chartFont, fontSize: 12, color: chrome.textSecondary }}
        />
        <Area
          type="monotone"
          dataKey={raisedLabel}
          stroke={series.blue}
          strokeWidth={2}
          fill="url(#fillRaised)"
          activeDot={{ r: 4, strokeWidth: 2, stroke: chrome.surface }}
        />
        <Area
          type="monotone"
          dataKey={resolvedLabel}
          stroke={series.aqua}
          strokeWidth={2}
          fill="url(#fillResolved)"
          activeDot={{ r: 4, strokeWidth: 2, stroke: chrome.surface }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default TrendChart;
